/**
 * Wrapper de rota protegida.
 *
 *   <ProtectedRoute>           → exige autenticação
 *   <ProtectedRoute role="admin">  → exige role >= admin
 *   <ProtectedRoute roles={["super_admin"]}>  → roles específicos
 */

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { Loading } from "./Loading";
import type { Role } from "@/types";

interface Props {
  children: ReactNode;
  /** Mínimo de role hierárquico exigido (viewer < operator < admin < super_admin). */
  role?: Role;
  /** Lista exata de roles permitidos (ignora hierarquia). */
  roles?: Role[];
}

export function ProtectedRoute({ children, role, roles }: Props) {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.initialized || auth.loading) {
    return <Loading />;
  }

  if (!auth.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && !auth.hasRole(...roles)) {
    return <Forbidden />;
  }
  if (role && !auth.hasMinRole(role)) {
    return <Forbidden />;
  }

  return <>{children}</>;
}

function Forbidden() {
  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="text-6xl">🔒</div>
        <h1 className="mt-4 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Acesso negado
        </h1>
        <p className="mt-2 text-slate-600 dark:text-slate-400">
          Você não tem permissão pra acessar essa área. Fala com seu admin.
        </p>
      </div>
    </div>
  );
}
